// src/components/SectionDon.jsx
import { useEffect, useState } from "react";
import { FaHeart, FaLock, FaServer, FaUsers, FaBullhorn } from "react-icons/fa";
import { toast } from "react-toastify";
import { creerDon, estimerNet, euros } from "../services/DonService";

const MONTANTS = [5, 10, 20, 50];
const MIN = 5;

const inputClass =
    "w-full rounded-xl bg-white text-black px-4 py-3 border-2 border-violet-300 focus:border-violet-600 outline-none transition";

export default function SectionDon() {
    const [montant, setMontant] = useState("10");
    const [nom, setNom] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);

    // Retour Stripe annulé
    useEffect(() => {
        const p = new URLSearchParams(window.location.search);
        if (p.get("don") === "annule") toast.info("Don annulé, aucun paiement n'a été effectué.");

        // Arrivée depuis une autre page via /#don → on descend jusqu'à la section
        // (petit délai : le temps que la page d'accueil finisse de s'afficher)
        if (window.location.hash === "#don") {
            setTimeout(() => {
                document.getElementById("don")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 400);
        }
    }, []);

    const valeur = parseFloat(String(montant).replace(",", ".")) || 0;
    const valide = valeur >= MIN;
    const { frais, net } = estimerNet(valide ? valeur : 0);

    const envoyer = async (e) => {
        e.preventDefault();
        if (!valide) return toast.error(`Le don minimum est de ${MIN} €.`);
        if (!nom.trim() || !email.trim()) return toast.error("Nom et email obligatoires.");

        setLoading(true);
        try {
            const { url } = await creerDon({ montant: valeur, nom, email, message });
            window.location.href = url; // → Stripe Checkout
        } catch (err) {
            toast.error(err.message);
            setLoading(false);
        }
    };

    return (
        <section id="don" className="scroll-mt-24 w-full bg-black text-white px-4 md:px-10 py-20">
            <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
                {/* ── Texte ── */}
                <div>
                    <span className="inline-flex items-center gap-2 bg-violet-900/40 text-violet-300 text-sm font-semibold px-4 py-1.5 rounded-full">
                        <FaHeart /> Soutenir l'association
                    </span>
                    <h2 className="font-archivo text-3xl md:text-5xl font-extrabold mt-5 leading-tight">
                        Aidez Thrundrz à <span className="text-violet-400">rester en ligne</span>
                    </h2>
                    <p className="text-gray-300 mt-5 text-lg leading-relaxed">
                        Thrundrz est porté par une association. Vos dons financent les serveurs,
                        le développement de l'application et la mise en avant des organisateurs locaux.
                        Chaque euro compte 💜
                    </p>

                    <div className="mt-8 space-y-4">
                        {[
                            { i: <FaServer />, t: "Serveurs & hébergement", d: "Garder l'app rapide et disponible 24h/24." },
                            { i: <FaUsers />, t: "Nouvelles fonctionnalités", d: "Améliorer l'app web et mobile." },
                            { i: <FaBullhorn />, t: "Visibilité locale", d: "Mettre en avant les petits organisateurs." },
                        ].map((x) => (
                            <div key={x.t} className="flex gap-4 items-start">
                                <div className="w-11 h-11 shrink-0 rounded-xl bg-violet-900/50 text-violet-300 flex items-center justify-center text-lg">
                                    {x.i}
                                </div>
                                <div>
                                    <p className="font-bold">{x.t}</p>
                                    <p className="text-gray-400 text-sm">{x.d}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Formulaire ── */}
                <form
                    onSubmit={envoyer}
                    className="bg-gradient-to-br from-[#1a0b33] to-[#0d0d0d] border border-violet-800/60 rounded-3xl p-6 md:p-8 shadow-2xl"
                >
                    <p className="font-bold text-lg mb-4">Choisissez votre montant</p>

                    <div className="grid grid-cols-4 gap-2 mb-4">
                        {MONTANTS.map((m) => {
                            const actif = valeur === m;
                            return (
                                <button
                                    type="button"
                                    key={m}
                                    onClick={() => setMontant(String(m))}
                                    className={`py-3 rounded-xl font-bold transition border-2 ${actif
                                            ? "bg-violet-600 border-violet-600 text-white"
                                            : "bg-transparent border-violet-700/60 text-violet-200 hover:border-violet-500"
                                        }`}
                                >
                                    {m} €
                                </button>
                            );
                        })}
                    </div>

                    <label className="text-sm text-gray-400">Montant libre (minimum {MIN} €)</label>
                    <div className="relative mt-1 mb-2">
                        <input
                            type="number"
                            inputMode="decimal"
                            min={MIN}
                            step="0.01"
                            value={montant}
                            onChange={(e) => setMontant(e.target.value)}
                            className={`${inputClass} pr-10 text-xl font-bold`}
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">€</span>
                    </div>
                    {!valide && montant !== "" && (
                        <p className="text-red-400 text-sm mb-2">Le don minimum est de {MIN} €.</p>
                    )}

                    {/* Ce qu'il nous reste */}
                    <div className="bg-black/40 rounded-2xl p-4 my-4 text-sm space-y-2">
                        <div className="flex justify-between text-gray-400">
                            <span>Votre don</span>
                            <span>{euros(valide ? Math.round(valeur * 100) : 0)}</span>
                        </div>
                        <div className="flex justify-between text-gray-400">
                            <span>Frais bancaires Stripe</span>
                            <span>− {euros(valide ? frais : 0)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-base border-t border-white/10 pt-2">
                            <span>Voilà ce qu'il nous reste 💜</span>
                            <span className="text-violet-400">{euros(net)}</span>
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3 mb-3">
                        <input className={inputClass} placeholder="Nom complet" value={nom} onChange={(e) => setNom(e.target.value)} required />
                        <input className={inputClass} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                    </div>
                    <textarea
                        className={`${inputClass} resize-none`}
                        rows={2}
                        maxLength={500}
                        placeholder="Un petit mot pour l'équipe ? (facultatif)"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                    />

                    <button
                        type="submit"
                        disabled={!valide || loading}
                        className="w-full mt-5 py-4 rounded-full font-bold text-lg bg-gradient-to-r from-[#481699] via-violet-700 to-violet-500 hover:opacity-90 disabled:opacity-40 transition flex items-center justify-center gap-2"
                    >
                        <FaHeart />
                        {loading ? "Redirection…" : `Faire un don de ${valide ? euros(Math.round(valeur * 100)) : "…"}`}
                    </button>

                    <p className="text-xs text-gray-500 text-center mt-3 flex items-center justify-center gap-1">
                        <FaLock /> Paiement sécurisé par Stripe · reçu envoyé par email
                    </p>
                </form>
            </div>
        </section>
    );
}